import React, { useCallback, useState } from 'react'

import { IActionType } from '@makyoapp/makyo-common'

import { IRow } from '@/core/models/tables/row'

import { ActionInputPanel, ActionOutputPanel } from '.'
import { useWorkflowNodeContext } from '../WorkflowActionFormModal'
import { PanelProps } from './panels/panel.type'

const ActFormWithInputOutputContext = React.createContext<
  | {
      data: PanelProps['data']
    }
  | undefined
>(undefined)

export function useActFormWithInputOutputContext() {
  const context = React.useContext(ActFormWithInputOutputContext)
  if (!context) {
    throw new Error('useActFormWithInputOutputContext must be used within a ActFormWithInputOutput')
  }
  return context
}

export default function ActFormWithInputOutput({
  children,
  data,
}: {
  children: React.ReactNode
  data: PanelProps['data']
}) {
  const [inputValue, setInputValue] = useState<string | IRow | null>(null)
  const nodeContext = useWorkflowNodeContext()

  const handleInputChange = useCallback(
    (value: PanelProps['data']) => {
      switch (nodeContext?.previousNode?.data?.actionValue?.type) {
        case IActionType.TemporaryVariable:
          setInputValue(value.value as string)
          break
        case IActionType.FilterData:
          setInputValue(value as IRow)
          break
      }
    },
    [nodeContext?.previousNode?.data?.actionValue?.type]
  )

  return (
    <div className='flex h-full min-h-[600px]'>
      {/* Left Panel - Input */}
      <ActionInputPanel
        setValue={handleInputChange}
        panelType='input'
        actionType={nodeContext?.previousNode?.data?.actionValue?.type}
      />

      {/* Center Panel - Dynamic Content */}
      <div className='flex-1 max-w-[678px]'>{children}</div>

      {/* Right Panel - Output */}
      <div className='w-[678px]'>
        <ActionOutputPanel
          data={data}
          panelType='output'
          actionType={nodeContext?.currentNode?.data?.actionValue?.type}
          inputValue={inputValue}
        />
      </div>
    </div>
  )
}
